import * as React from "react";
import { Form, Input, InputOnChangeData } from "semantic-ui-react";
import { validate } from "not-valid";
import { isEqual } from "lodash";

import { QuestionProps, AnsweredQuestion } from "../../model";
import { isQuestionDisabled } from "../../questionnaireConfig/models";

import { QuestionWrapper } from "./QuestionWrapper";

interface NumberQuestionProps extends QuestionProps {
    min?: number;
    max?: number;
    noLabel?: boolean;
}

interface NumberQuestionState {
    showError: boolean;
    error: string;
}

class NumberQuestion extends React.Component<NumberQuestionProps, NumberQuestionState> {
    constructor(props: NumberQuestionProps) {
        super(props);

        this.state = {
            showError: false,
            error: null
        };

        this.onChange = this.onChange.bind(this);
        this.onBlur = this.onBlur.bind(this);
    }

    public componentDidMount() {
        if (this.props.readonly) {
            this.validateAndUpdateQuestion(this.props.answer as number);
        }
    }

    public componentDidUpdate(oldProps: QuestionProps) {
        if (!isEqual(oldProps.answer, this.props.answer)) {
            this.validateAndUpdateQuestion(this.props.answer as number);
        }
    }

    public render() {
        if (this.props.hidden || isQuestionDisabled(this.props.questionConfig)) {
            return null;
        }

        const error = this.state.showError ? this.state.error : null;
        const answerNotGiven = !(this.props.answer || this.props.answer === 0);
        return (
            <QuestionWrapper
                inline={this.props.inline}
                question={this.props.questionConfig.questionText}
                mandatory={this.props.mandatory}
                hideDivider={this.props.hideDivider}
                error={((this.state.showError || this.props.showError) && this.props.mandatory && answerNotGiven && "This field must be completed"
                    || error)}
                questionClassName={this.props.questionClassName}
                noLabel={this.props.noLabel}
                readonly={this.props.readonly}
                removable={{
                    ...this.props.removable,
                    questionId: this.props.questionConfig.id,
                    isRemovedByUser: this.props.isRemovedByUser
                }}
                outdated={{
                    ...this.props.outdated,
                    questionId: this.props.questionConfig.id
                }}
            >
                <Form.Field error={!!error}>
                    <div className="ui input">
                        {this.renderValue()}
                    </div>
                </Form.Field>
            </QuestionWrapper>
        );
    }

    private renderValue() {
        if (this.props.readonly) {
            return <div>{this.props.answer}</div>;
        }
        return (
            <Input
                type="number"
                value={this.props.answer}
                onChange={this.onChange}
                onBlur={this.onBlur}
                min={this.props.min}
                max={this.props.max}
            />
        );
    }

    private onChange(event: React.FormEvent<HTMLInputElement>, data: InputOnChangeData) {
        const answer = data.value && parseFloat(data.value);
        this.validateAndUpdateQuestion(answer);
    }

    private validateAndUpdateQuestion(answer: number) {
        const { questionConfig, updateQuestion } = this.props;
        if (isQuestionDisabled(questionConfig)) {
            return;
        }

        const isAnswerGiven = answer || answer === 0;
        const isMandatoryValid = (isAnswerGiven && !isNaN(answer)) || (!this.props.mandatory && !isAnswerGiven);
        let error;
        if (!isMandatoryValid) {
            error = isMandatoryValid ? null : "Please enter a valid number";
        }

        const hasRangeValidation = (this.props.min || this.props.min === 0) && (this.props.max || this.props.max === 0);
        const isRangeValid = !isAnswerGiven || (!hasRangeValidation || (isMandatoryValid && answer >= this.props.min && answer <= this.props.max));
        if (!isRangeValid) {
            error = isRangeValid ? null : `Please enter a value between ${this.props.min} and ${this.props.max}`;
        }

        const isValid = isMandatoryValid && isRangeValid;
        this.setState({ error });

        const newQuestion: AnsweredQuestion = { question: questionConfig.questionText, answer };
        if (!isValid || !(this.props.validators && this.props.validators.length)) {
            newQuestion.isValid = isValid;
        }
        updateQuestion(newQuestion);
        if (this.props.onAnswerChange) {
            this.props.onAnswerChange(newQuestion.answer);
        }

        if (isValid && this.props.validators && this.props.validators.length && !isNaN(answer)) {
            this.props.validateQuestion(this.getValidatedQuestion(questionConfig.questionText, answer));
        }
    }

    private async getValidatedQuestion(question: string, answer: number): Promise<AnsweredQuestion> {
        const errors = await validate(this.props.validators || [], answer);
        this.setState({ error: errors[0] });
        const isValid = errors.length === 0 && (!!answer || !this.props.mandatory);
        return { question, answer, isValid };
    }

    private onBlur() {
        this.setState({ showError: true });
    }
}

export { NumberQuestion };
